// The Pages Router builds every client-side data request as
// `addBasePath('/_next/data/<buildId>/<route>.json')` (next/dist/client/page-loader),
// which is fetched through the global `fetch` (next/dist/shared/lib/router/router
// `fetchRetry`). We run with NO `basePath` — Pepyaka strips the `/orion-web` /
// `/symphony-web` mount before the pod sees a request, and a single `basePath`
// value can't front the manage.wix.com mounts and the symphony.wix.com root at
// once — so that URL resolves to the bare host root
// (`manage.wix.com/_next/data/...`), which Pepyaka routes to the Business
// Manager shell, not our pod. Every client navigation's data fetch then 404s and
// Next "recovers" by hard-navigating to the current URL, throwing "attempted to
// hard navigate to the same URL" and wedging the page. Static chunks escape this
// via `assetPrefix`, but `assetPrefix` is never applied to data requests.
//
// Re-add the active mount prefix on the wire — the pod serves
// `/<mount>/_next/data/...` correctly — so client navigation lands instead of
// 404ing. On the root mount (symphony.wix.com) the prefix is the empty string,
// so the data URL is left at the root, which the root mapping routes to the pod.
// For a data route that 404s even when prefixed (a tab whose buildId predates a
// deploy), do the full reload Next intended, rather than letting its same-URL
// hard-nav throw.

import { resolveMount, type Mount } from './resolve-mount';

const DATA_ROUTE_PREFIX = '/_next/data/';
const STALE_RELOAD_KEY = 'orion-web:next-data-stale-reload';

function activeMount(pathname: string): Mount {
  return resolveMount(pathname);
}

let installed = false;

export function installNextDataMountPrefix(): void {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    // Only string / URL inputs reach here for data requests; a Request object
    // (with its own method/body) is passed through untouched.
    if (typeof input === 'string' || input instanceof URL) {
      try {
        const url = new URL(input.toString(), window.location.href);
        if (url.origin === window.location.origin && url.pathname.startsWith(DATA_ROUTE_PREFIX)) {
          url.pathname = `${activeMount(window.location.pathname)}${url.pathname}`;
          return originalFetch(url.href, init).then(reloadOnStaleBuild);
        }
      } catch {
        // not a parseable URL — fall through to the untouched call
      }
    }
    return originalFetch(input, init);
  };
}

function reloadOnStaleBuild(res: Response): Response {
  if (res.status !== 404) return res;
  // A prefixed data route that still 404s means the buildId is gone (the tab
  // predates a deploy). Reload once — guarded so a genuinely-broken route can't
  // loop — to pick up the current buildId.
  try {
    const last = Number(sessionStorage.getItem(STALE_RELOAD_KEY) ?? 0);
    if (Date.now() - last < 10_000) return res;
    sessionStorage.setItem(STALE_RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable — a single reload is still safe enough
  }
  window.location.reload();
  return res;
}
